/**
 * avatarViewStore — how the rig avatar is DISPLAYED in the viewport, as opposed
 * to what it is: whether the skeleton's joints are drawn over it, and whether its
 * meshes render lit (the real materials), unlit (base colour only, the way
 * Unreal's Unlit view mode reads a material's colour input), or as wireframe.
 *
 * Editor-only viewport modes. Nothing here is persisted or shared: a view mode
 * is a question you ask of the scene in front of you, not a property of the
 * character, so it resets with the page like the camera-cage gizmo does.
 *
 * Module singleton + subscribe, like navSettingsStore: the outliner rows
 * (Armature: Display joints; Meshes: Lit / Unlit / Wireframe) declare the
 * controls, App performs them here, and the in-Canvas components read the state
 * to draw the joints and re-flag the materials. Exposed as `window.__AVATAR_VIEW__`
 * so a console session or a spec can flip the same switches the rows do.
 */

export const MESH_VIEWS = ['lit', 'unlit', 'wireframe']

let state = {
  showJoints: false,
  meshView: 'lit',
}
const listeners = new Set()

function emit() {
  for (const l of listeners) l(state)
}

export const avatarViewStore = {
  getState() { return state },
  subscribe(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },
  setShowJoints(on) {
    const v = !!on
    if (v === state.showJoints) return
    state = { ...state, showJoints: v }
    emit()
  },
  setMeshView(mode) {
    if (!MESH_VIEWS.includes(mode)) {
      throw new Error(`avatarViewStore: unknown mesh view "${mode}" — one of ${MESH_VIEWS.join(', ')}`)
    }
    if (mode === state.meshView) return
    state = { ...state, meshView: mode }
    emit()
  },
  /** Back to the plain viewport. Used by tests and by a character switch. */
  reset() {
    if (!state.showJoints && state.meshView === 'lit') return
    state = { showJoints: false, meshView: 'lit' }
    emit()
  },
}

if (typeof window !== 'undefined') {
  window.__AVATAR_VIEW__ = avatarViewStore
}
